import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { format } from 'date-fns';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';

import type { Task } from '../entities/task';
import type { Workstream } from '../entities/workstream';
import { TasksQuery } from '../queries/tasks.query';
import { WorkstreamQuery } from '../queries/workstream.query';
import { WorkstreamsQuery } from '../queries/workstreams.query';

export namespace CreateTaskMutation {
  export type Input = {
    siteId: string;
    workstreamId: Workstream['id'];
    create: Pick<
      Task,
      'title' | 'description' | 'status' | 'priority' | 'dueDate'
    >;
  };
  export type Output = void;

  function useMutationFn() {
    const createTask = useInjectFeApiContext(FeApi.tasksV1CardsNewCreate);

    return async function mutationFn(input: Input) {
      await ok(
        createTask({
          createCardViewInputRequest: {
            // Defaults
            card_type: 'Standard',
            workstream_uuid: input.workstreamId,
            // Input
            title: input.create.title,
            description: input.create.description,
            status: input.create.status,
            priority: input.create.priority,
            due_date: input.create.dueDate
              ? format(input.create.dueDate, 'yyyy-MM-dd')
              : undefined,
          },
        }),
      );
    };
  }

  export function useMutation() {
    const queryClient = rq.useQueryClient();
    const mutationFn = useMutationFn();

    return rq.useMutation<Output, unknown, Input>({
      mutationFn,
      async onSuccess(_, input) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: WorkstreamsQuery.key({}) }),
          queryClient.invalidateQueries({
            queryKey: WorkstreamQuery.key({ id: input.workstreamId }),
          }),
          queryClient.invalidateQueries({
            queryKey: TasksQuery.key({ siteId: input.siteId }),
          }),
        ]);
      },
    });
  }
}
